import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import { huHU } from '@mui/x-data-grid/locales';
import { isEqual } from 'lodash';
import {
  DataGridPremium,
  useGridApiRef,
  GridToolbarContainer,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridToolbarColumnsButton,
  GridLogicOperator,
  gridColumnFieldsSelector,
  GridToolbarExport,
} from '@mui/x-data-grid-premium';
import { useTheme } from '@mui/material/styles';

import { datagridLangHunCustomized } from '../../../config/datagridLangHunCustomized';
import useSaveView from '../../../hooks/useSaveView';
import { getDataGridStyles } from '../Datagrid/dataGridStyles';
import { DataGridIdentifiers } from '../Datagrid/DataGridIdentifiers';
import useUsers from '../../../context/UsersContext';
import savedDataGridViewService from '../../../service/savedDataGridView.service';
import addNotification from '../../../utils/addNotification';
import { NotificationType } from '../../../config';
import SavedDataGridViewsToolbar from './SavedDataGridViewsToolbar';

const EMPTY_FILTER = { filterModel: { items: [], logicOperator: 'and' } };
const EMPTY_SORTING = { sortModel: [] };

const parseSavedViewState = (state) => {
  if (!state) return {};
  if (typeof state !== 'string') return state;

  try {
    return JSON.parse(state);
  } catch (_error) {
    return {};
  }
};

const getSavedViewState = (state, columns) => {
  const columnFields = new Set((columns ?? []).map((column) => column.field));
  const filter = state?.filter ?? EMPTY_FILTER;
  const sorting = state?.sorting ?? EMPTY_SORTING;

  return {
    filter: {
      ...filter,
      filterModel: {
        ...filter.filterModel,
        items: (filter.filterModel?.items ?? []).filter((item) => columnFields.has(item.field)),
      },
    },
    sorting: {
      ...sorting,
      sortModel: (sorting.sortModel ?? []).filter((item) => columnFields.has(item.field)),
    },
  };
};

const DataGridToolbar = ({
  gridExport,
  customHeaderComponent,
  enableSavedViews,
  savedGridViews,
  selectedSavedViewId,
  currentUserId,
  isSavedViewDirty,
  isSavedViewsSelectOpen,
  savedViewToDelete,
  refreshSavedGridViews,
  setIsSavedViewsSelectOpen,
  setSavedViewToDelete,
  applySavedGridView,
  saveCurrentGridView,
  deleteSavedGridView,
}) => (
  <GridToolbarContainer>
    <GridToolbarColumnsButton />
    <GridToolbarFilterButton />
    <GridToolbarDensitySelector />
    {gridExport && <GridToolbarExport />}
    {customHeaderComponent}
    {enableSavedViews && (
      <SavedDataGridViewsToolbar
        views={savedGridViews}
        selectedViewId={selectedSavedViewId}
        currentUserId={currentUserId}
        isDirty={isSavedViewDirty}
        isOpen={isSavedViewsSelectOpen}
        viewToDelete={savedViewToDelete}
        onOpen={refreshSavedGridViews}
        onOpenChange={setIsSavedViewsSelectOpen}
        onViewToDeleteChange={setSavedViewToDelete}
        onSelect={applySavedGridView}
        onSave={saveCurrentGridView}
        onDelete={deleteSavedGridView}
      />
    )}
  </GridToolbarContainer>
);

const DataGridPremiumUI = ({
  datagridIdentifier,
  dataLoader,
  paginationAndfilterMode = 'client',
  datagridInitialState,
  refetch = { refetchTable: false, setRefetchTable: () => {} },
  toolbar,
  gridExport,
  filterFromInitialState,
  customHeaderComponent,
  disableSave = false,
  enableSavedViews = false,
  ...props
}) => {
  const theme = useTheme();
  const arf = useGridApiRef();
  const apiRef = props.apiRef ?? arf;
  const { refetchTable, setRefetchTable } = refetch;
  const parentNode = document.body.parentNode;
  const height = parentNode.clientHeight * 0.85;

  const { getUser, user } = useUsers();
  const [isLoading, setisLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [queryOptions, setQueryOptions] = useState({
    pagination: {
      paginationModel: {
        pageSize: 25,
        page: 0,
      },
    },
    filter: { filterModel: { items: [], logicOperator: 'and' } },
    sorting: {
      sortModel: [],
    },
  });

  const [initialState, setInitialState] = useState({});
  const pendingRestoreStateRef = useRef(null);
  const isApplyingPendingRestoreRef = useRef(false);
  const saveViewToDb = useSaveView(user);
  const [savedGridViews, setSavedGridViews] = useState([]);
  const [savedGridViewsLoaded, setSavedGridViewsLoaded] = useState(!enableSavedViews);
  const [savedGridViewsLoadFailed, setSavedGridViewsLoadFailed] = useState(false);
  const [isSavedViewsSelectOpen, setIsSavedViewsSelectOpen] = useState(false);
  const [savedViewToDelete, setSavedViewToDelete] = useState(null);
  const [selectedSavedViewId, setSelectedSavedViewId] = useState(null);
  const [currentSavedViewState, setCurrentSavedViewState] = useState(null);

  const selectedSavedView = savedGridViews.find((view) => view.id === selectedSavedViewId);
  const selectedSavedViewState = selectedSavedView
    ? getSavedViewState(parseSavedViewState(selectedSavedView.state), props.columns)
    : null;
  const isSavedViewDirty = Boolean(
    selectedSavedViewState && currentSavedViewState && !isEqual(selectedSavedViewState, currentSavedViewState)
  );

  const saveView = (viewType, viewData) => {
    if (disableSave) return;
    if (Object.keys(user).length > 0) {
      if (filterFromInitialState) {
        viewData = {
          [datagridIdentifier]: {
            ...viewData[datagridIdentifier],
            filter: { filterModel: { items: [], logicOperator: 'and' } },
            pagination: {
              paginationModel: {
                pageSize: 25,
                page: 0,
              },
            },
          },
        };
      }

      saveViewToDb(viewType, viewData);
    }
  };

  useEffect(() => {
    if (!Object.keys(user).length) getUser();
  }, []);

  useEffect(() => {
    if (!enableSavedViews) return;

    let active = true;
    setSavedGridViewsLoaded(false);
    setSavedGridViewsLoadFailed(false);
    savedDataGridViewService
      .getViews(datagridIdentifier)
      .then((views) => {
        if (active) setSavedGridViews(views);
      })
      .catch(() => {
        if (active) setSavedGridViewsLoadFailed(true);
      })
      .finally(() => {
        if (active) setSavedGridViewsLoaded(true);
      });

    return () => {
      active = false;
    };
  }, [enableSavedViews, datagridIdentifier]);

  useEffect(() => {
    if (!enableSavedViews || !Object.keys(user).length) return;

    const storedViewId = user.views?.selectedDataGridViews?.[datagridIdentifier];
    setSelectedSavedViewId(storedViewId === null || storedViewId === undefined ? null : Number(storedViewId));
  }, [enableSavedViews, user, datagridIdentifier]);

  const saveSelectedViewId = async (viewId) => {
    setSelectedSavedViewId(viewId);
    await saveViewToDb('selectedDataGridViews', { [datagridIdentifier]: viewId });
  };

  const useColumnsState = (apiRef, columns) => {
    const [widths, setWidths] = useState({});
    const [orderedFields, setOrderedFields] = useState(columns.map((column) => column.field));

    const onColumnWidthChange = React.useCallback(
      ({ colDef, width }) => {
        if (disableSave) {
          setWidths((prev) => ({ ...prev, [colDef.field]: width }));
          return;
        }
        setWidths((prev) => ({ ...prev, [colDef.field]: width }));
        const currentState = apiRef.current.exportState();
        saveView('dataGrid', { [datagridIdentifier]: currentState });
      },
      [setWidths, disableSave]
    );

    const onColumnOrderChange = React.useCallback(() => {
      if (disableSave) {
        setOrderedFields(gridColumnFieldsSelector(apiRef));
        return;
      }
      setOrderedFields(gridColumnFieldsSelector(apiRef));
      const currentState = apiRef.current.exportState();
      saveView('dataGrid', { [datagridIdentifier]: currentState });
    }, [apiRef, setOrderedFields, disableSave]);

    // Sync columns when props.columns changes (add new, remove missing, keep widths for existing)
    useEffect(() => {
      const currentFields = columns.map((c) => c.field);
      setOrderedFields((prev) => {
        const updatedFields = [
          ...prev.filter((f) => currentFields.includes(f)),
          ...currentFields.filter((f) => !prev.includes(f)),
        ];
        return isEqual(prev, updatedFields) ? prev : updatedFields;
      });
      setWidths((prev) => {
        const next = {};
        currentFields.forEach((f) => {
          if (prev[f] !== undefined) next[f] = prev[f];
        });
        return next;
      });
    }, [columns]);

    const computedColumns = React.useMemo(
      () =>
        orderedFields.reduce((acc, field) => {
          const column = columns.find((col) => col.field === field);
          if (!column) return acc;
          if (widths[field]) {
            acc.push({ ...column, width: widths[field] });
            return acc;
          }
          acc.push(column);
          return acc;
        }, []),
      [columns, widths, orderedFields]
    );

    return { columns: computedColumns, onColumnWidthChange, onColumnOrderChange, setOrderedFields };
  };

  const columnsState = useColumnsState(apiRef, props.columns);

  const handleEvent = (_params, _event, _details) => {
    if (disableSave) return;
    if (Object.keys(_params).length === 0) return;

    const currentState = apiRef.current.exportState();
    setInitialState(currentState);
    if (enableSavedViews) setCurrentSavedViewState(getSavedViewState(currentState, props.columns));
    saveView('dataGrid', { [datagridIdentifier]: currentState });
  };

  const abortControllerRef = useRef();

  const loader = async (tableState) => {
    setisLoading(true);
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;
    try {
      if (tableState.pagination?.paginationModel?.pageSize === -1 && paginationAndfilterMode === 'server') {
        delete tableState.pagination.paginationModel;
      }
      await dataLoader(tableState, controller.signal);
    } catch (err) {
      if (err.name !== 'CanceledError' && err.name !== 'AbortError') {
        // Only log if not a cancellation
        console.error(err);
      }
    } finally {
      setisLoading(false);
    }
  };

  useEffect(() => {
    if (Object.keys(apiRef?.current).length > 0 && !disableSave) {
      const subscriptions = [
        apiRef.current.subscribeEvent('aggregationModelChange', handleEvent),
        apiRef.current.subscribeEvent('columnVisibilityModelChange', handleEvent),
        apiRef.current.subscribeEvent('columnOrderChange', handleEvent),
        apiRef.current.subscribeEvent('columnWidthChange', handleEvent),
        apiRef.current.subscribeEvent('rowGroupingModelChange', handleEvent),
        apiRef.current.subscribeEvent('sortModelChange', handleEvent),
        apiRef.current.subscribeEvent('onDensityChange', handleEvent),
        apiRef.current.subscribeEvent('pinnedColumnsChange', handleEvent),
      ];
      if (!filterFromInitialState) subscriptions.push(apiRef.current.subscribeEvent('filterModelChange', handleEvent));
      if (!filterFromInitialState) subscriptions.push(apiRef.current.subscribeEvent('paginationModelChange', handleEvent));

      return () => subscriptions.forEach((unsubscribe) => unsubscribe());
    }
  }, [apiRef.current, disableSave, enableSavedViews, filterFromInitialState]);

  useEffect(() => {
    if (disableSave) return;
    const savedDatagridState = apiRef.current.exportState();
    if (savedDatagridState && Object.keys(apiRef?.current).length > 0) {
      if (paginationAndfilterMode === 'client' && props.rows.length > 0) {
        const isNormalPagination = [25, 50, 100].includes(savedDatagridState.pagination.paginationModel.pageSize);
        if (!isNormalPagination) {
          savedDatagridState.pagination.paginationModel.pageSize = props.rows.length > 0 ? props.rows.length : 25;
          savedDatagridState.pagination.paginationModel.page = 0;
        }
      }
      apiRef.current.restoreState(savedDatagridState);
    }
  }, [props.rows.length, disableSave]);

  useEffect(() => {
    if (Object.keys(user).length > 0 && isInitialLoad && (!enableSavedViews || savedGridViewsLoaded)) {
      if (disableSave) {
        const options = {
          filter: datagridInitialState?.filter,
          pagination: datagridInitialState?.pagination,
          sorting: datagridInitialState?.sorting,
        };
        setQueryOptions(options);
        if (paginationAndfilterMode === 'server') {
          loader(options);
        }
        setIsInitialLoad(false);
        return;
      }

      const savedDatagridState = user.views?.dataGrid?.[datagridIdentifier];
      const storedSavedViewId = user.views?.selectedDataGridViews?.[datagridIdentifier];
      const storedSavedView = enableSavedViews
        ? savedGridViews.find((view) => view.id === Number(storedSavedViewId))
        : null;
      const storedSavedViewState = storedSavedView
        ? getSavedViewState(parseSavedViewState(storedSavedView.state), props.columns)
        : null;
      const restoredDatagridState = storedSavedViewState
        ? {
            ...(savedDatagridState ?? datagridInitialState),
            ...storedSavedViewState,
            pagination: {
              paginationModel: {
                ...(savedDatagridState?.pagination?.paginationModel ??
                  datagridInitialState?.pagination?.paginationModel),
                page: 0,
              },
            },
          }
        : savedDatagridState;

      if (
        enableSavedViews &&
        !savedGridViewsLoadFailed &&
        storedSavedViewId !== null &&
        storedSavedViewId !== undefined &&
        !storedSavedView
      ) {
        saveSelectedViewId(null);
      }

      if (restoredDatagridState) {
        const options = {
          filter: !filterFromInitialState
            ? restoredDatagridState?.filter ?? datagridInitialState?.filter
            : datagridInitialState?.filter,
          pagination: !filterFromInitialState
            ? restoredDatagridState?.pagination ?? datagridInitialState?.pagination
            : datagridInitialState?.pagination,
          sorting: !filterFromInitialState
            ? restoredDatagridState?.sorting ?? datagridInitialState?.sorting
            : datagridInitialState?.sorting,
        };

        setInitialState(restoredDatagridState);
        if (enableSavedViews) setCurrentSavedViewState(getSavedViewState(restoredDatagridState, props.columns));
        pendingRestoreStateRef.current = restoredDatagridState;
        if (Object.keys(apiRef?.current ?? {}).length > 0) {
          apiRef.current.restoreState(restoredDatagridState);
          apiRef.current.hidePreferences();
          pendingRestoreStateRef.current = null;
        }

        const currentFields = props.columns.map((col) => col.field);
        const savedFields = restoredDatagridState?.columns?.orderedFields;
        const newFields = currentFields.filter((field) => !savedFields?.includes(field));
        const updatedFields = [...(savedFields?.filter((field) => currentFields.includes(field)) ?? []), ...newFields];

        // Ha van változás, mentsük el az új állapotot
        if (!isEqual(savedFields, updatedFields) && restoredDatagridState.columns) {
          restoredDatagridState.columns.orderedFields = updatedFields;
          saveView('dataGrid', { [datagridIdentifier]: restoredDatagridState });
        }

        if (!restoredDatagridState?.columns) {
          const dg = apiRef.current.exportState();
          dg.columns.orderedFields = updatedFields;
          saveView('dataGrid', { [datagridIdentifier]: dg });
        }

        columnsState.setOrderedFields(updatedFields);

        setQueryOptions(options);

        if (paginationAndfilterMode === 'server') {
          loader(options);
        }

        setIsInitialLoad(false);
      } else if (!restoredDatagridState) {
        const options = {
          filter: datagridInitialState?.filter,
          pagination: datagridInitialState?.pagination,
          sorting: datagridInitialState?.sorting,
        };
        setQueryOptions(options);
        if (!savedDatagridState?.columns && Object.keys(apiRef?.current ?? {}).length > 0) {
          const dg = apiRef.current.exportState();
          //dg.columns.orderedFields = updatedFields;
          saveView('dataGrid', { [datagridIdentifier]: dg });
        }
        if (paginationAndfilterMode === 'server') {
          loader(options);
        }
        setIsInitialLoad(false);
      }
    }
  }, [
    datagridIdentifier,
    user,
    isInitialLoad,
    apiRef,
    datagridInitialState,
    paginationAndfilterMode,
    loader,
    props.columns,
    saveView,
    disableSave,
    enableSavedViews,
    savedGridViews,
    savedGridViewsLoaded,
    savedGridViewsLoadFailed,
  ]);

  useEffect(() => {
    if (!isInitialLoad && refetchTable) {
      loader(queryOptions);
      setRefetchTable(false);
    }
  }, [refetchTable]);

  const refreshSavedGridViews = async () => {
    try {
      const views = await savedDataGridViewService.getViews(datagridIdentifier);
      setSavedGridViews(views);
      setSavedGridViewsLoadFailed(false);
    } catch (_error) {
      // A korábban betöltött lista backendhiba esetén is használható marad.
    }
  };

  const applySavedGridView = async (viewId) => {
    const view = savedGridViews.find((savedView) => savedView.id === viewId);
    const currentState = apiRef.current.exportState();
    const viewState = view ? getSavedViewState(parseSavedViewState(view.state), props.columns) : null;
    const personalState = user.views?.dataGrid?.[datagridIdentifier];
    const stateToRestore = viewState
      ? { ...currentState, ...viewState }
      : {
          ...(personalState ?? datagridInitialState ?? currentState),
          filter: EMPTY_FILTER,
        };
    const paginationModel = {
      ...(stateToRestore?.pagination?.paginationModel ?? queryOptions?.pagination?.paginationModel),
      page: 0,
    };
    const restoredState = {
      ...stateToRestore,
      pagination: { paginationModel },
    };
    const options = {
      filter: restoredState.filter ?? EMPTY_FILTER,
      sorting: restoredState.sorting ?? EMPTY_SORTING,
      pagination: restoredState.pagination,
    };

    setInitialState(restoredState);
    setCurrentSavedViewState(getSavedViewState(restoredState, props.columns));
    setQueryOptions(options);
    isApplyingPendingRestoreRef.current = true;
    apiRef.current.restoreState(restoredState);
    apiRef.current.hidePreferences();
    queueMicrotask(() => {
      isApplyingPendingRestoreRef.current = false;
    });

    const selectedViewSave = saveSelectedViewId(viewId);
    if (paginationAndfilterMode === 'server') await loader(options);
    await selectedViewSave;
  };

  const saveCurrentGridView = async (name) => {
    const state = getSavedViewState(apiRef.current.exportState(), props.columns);
    const isOwner = selectedSavedView?.authorUserId === user.userId;

    if (selectedSavedView && isOwner && !name) {
      const updatedView = await savedDataGridViewService.updateView(selectedSavedView.id, {
        name: selectedSavedView.name,
        state,
        schemaVersion: 1,
      });
      setSavedGridViews((views) => views.map((view) => (view.id === updatedView.id ? updatedView : view)));
      setCurrentSavedViewState(state);
      addNotification({ content: 'A nézet frissítése sikeres.', type: NotificationType.SUCCESS });
      return;
    }

    const createdView = await savedDataGridViewService.createView({
      gridIdentifier: datagridIdentifier,
      name,
      state,
      schemaVersion: 1,
      copiedFromViewId: selectedSavedView?.id ?? null,
    });
    setSavedGridViews((views) => [...views, createdView]);
    setCurrentSavedViewState(state);
    await saveSelectedViewId(createdView.id);
    addNotification({ content: 'A nézet mentése sikeres.', type: NotificationType.SUCCESS });
  };

  const deleteSavedGridView = async (viewId) => {
    await savedDataGridViewService.deleteView(viewId);
    setSavedGridViews((views) => views.filter((view) => view.id !== viewId));
    if (selectedSavedViewId === viewId) await applySavedGridView(null);
    addNotification({ content: 'A nézet törlése sikeres.', type: NotificationType.SUCCESS });
  };

  if (!datagridIdentifier) {
    console.error('datagridIdentifier is required but was not provided.');
    return null; // Prevent the component from rendering if the identifier is not provided
  }
  if (!paginationAndfilterMode) {
    console.error('paginationAndfilterMode is required but was not provided.');
    return null; // Prevent the component from rendering if the paginationAndFilterMode is not provided
  }

  const disableEdit = () => {
    const rowsInEditMode = Object.keys(apiRef.current.state.editRows);

    for (const rowId of rowsInEditMode) {
      apiRef.current.stopRowEditMode({ id: rowId });
    }
  };

  const onFilterModelChange = (filterModel) => {
    if (isApplyingPendingRestoreRef.current) return;
    disableEdit();
    if (!isInitialLoad) {
      const filteredItems = filterModel.items.filter((item) => {
        if (item.operator === 'isEmpty' || item.operator === 'isNotEmpty') {
          return true;
        }
        if (item.operator === 'isAnyOf') {
          return Array.isArray(item.value) && item.value.length > 0;
        }
        return item.value !== undefined && item.value !== null;
      });
      const newModel = { ...filterModel, items: filteredItems };

      const options = { ...queryOptions, filter: { filterModel: newModel } };

      setQueryOptions(options);
      if (paginationAndfilterMode === 'server') {
        loader(options);
      }
    }
  };

  const onPaginationModelChange = (paginationModel) => {
    if (isApplyingPendingRestoreRef.current) return;
    disableEdit();
    if (!isInitialLoad) {
      let options;
      if (paginationModel.pageSize === -1 && paginationAndfilterMode === 'server') {
        options = { ...queryOptions, pagination: {} };
      } else {
        options = { ...queryOptions, pagination: { paginationModel } };
      }
      setQueryOptions(options);
      if (paginationAndfilterMode === 'server') {
        loader(options);
      }
    }
  };

  const onSortModelChange = (sortModel) => {
    if (isApplyingPendingRestoreRef.current) return;
    disableEdit();
    if (!isInitialLoad) {
      const options = { ...queryOptions, sorting: { sortModel } };
      setQueryOptions(options);
      if (paginationAndfilterMode === 'server') {
        loader(options);
      }
    }
  };

  const handleCellKeyDown = (params, event) => {
    props.onCellKeyDown?.(params, event);

    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;

    queueMicrotask(() => {
      const focusedCell = apiRef.current.state.focus.cell;
      if (!focusedCell?.id) return;

      const row = apiRef.current.getRow(focusedCell.id);
      if (!row) return;

      props.onRowClick?.({
        id: focusedCell.id,
        row,
      });

      props.onRowSelectionModelChange?.([focusedCell.id]);
    });
  };

  return (
    <DataGridPremium
      {...props}
      onCellKeyDown={handleCellKeyDown}
      onStateChange={(...args) => {
        if (isApplyingPendingRestoreRef.current) {
          props.onStateChange?.(...args);
          return;
        }

        if (pendingRestoreStateRef.current && Object.keys(apiRef?.current ?? {}).length > 0) {
          isApplyingPendingRestoreRef.current = true;
          apiRef.current.restoreState(pendingRestoreStateRef.current);
          apiRef.current.hidePreferences();
          pendingRestoreStateRef.current = null;
          queueMicrotask(() => {
            isApplyingPendingRestoreRef.current = false;
          });
        }
        props.onStateChange?.(...args);
      }}
      pagination={props.pagination ?? true}
      columns={columnsState.columns}
      onColumnWidthChange={columnsState.onColumnWidthChange}
      onColumnOrderChange={columnsState.onColumnOrderChange}
      apiRef={apiRef}
      //initialState={filterFromInitialState ? { datagridInitialState } : { ...datagridInitialState, ...initialState }}
      initialState={{ ...datagridInitialState, ...initialState }}
      rowHeight={45}
      sx={{ ...getDataGridStyles(theme, { height: props.height ?? height }), ...props.sx }}
      slots={{ toolbar: DataGridToolbar, ...props.slots }}
      slotProps={{
        ...props.slotProps,
        toolbar: {
          ...props.slotProps?.toolbar,
          gridExport,
          customHeaderComponent,
          enableSavedViews,
          savedGridViews,
          selectedSavedViewId: selectedSavedView ? selectedSavedViewId : null,
          currentUserId: user.userId,
          isSavedViewDirty,
          isSavedViewsSelectOpen,
          savedViewToDelete,
          refreshSavedGridViews,
          setIsSavedViewsSelectOpen,
          setSavedViewToDelete,
          applySavedGridView,
          saveCurrentGridView,
          deleteSavedGridView,
        },
        columnsPanel: {
          sx: {
            '& .MuiDataGrid-panelFooter button:first-child': {
              display: 'none',
            },
          },
        },
        filterPanel: {
          logicOperators: [GridLogicOperator.And],
        },
        pagination: {
          showFirstButton: true,
          showLastButton: true,
        },
      }}
      componentsProps={{
        columnMenu: {
          hide: true, // Tiltja az oszlop panel megnyitását
        },
      }}
      localeText={{ ...huHU.components.MuiDataGrid.defaultProps.localeText, ...datagridLangHunCustomized }}
      disableSelectionOnClick
      headerFilters={props.headerFilters ?? true}
      onFilterModelChange={onFilterModelChange}
      onPaginationModelChange={onPaginationModelChange}
      onSortModelChange={onSortModelChange}
      paginationMode={paginationAndfilterMode}
      sortingMode={paginationAndfilterMode}
      filterMode={paginationAndfilterMode}
      loading={props.loading || isLoading}
      pageSizeOptions={[25, 50, 100].concat(
        gridExport ? [{ value: paginationAndfilterMode === 'client' ? props.rows.length : -1, label: 'Összes' }] : []
      )}
    />
  );
};

DataGridPremiumUI.propTypes = {
  datagridIdentifier: PropTypes.oneOf(Object.values(DataGridIdentifiers)).isRequired,
  paginationAndfilterMode: PropTypes.oneOf(['server', 'client']).isRequired,
  disableSave: PropTypes.bool,
  enableSavedViews: PropTypes.bool,
};

export default DataGridPremiumUI;
